import { useEffect, useRef, useCallback, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa'

const THUMB_W = 64
const THUMB_H = 48

export default function GalleryModal({ images = [], isOpen, onClose, loading = false, initialIndex = 0 }) {
  const [current, setCurrent] = useState(0)
  const [direction, setDirection] = useState(1)
  const stripRef = useRef(null)
  const total = images.length

  const navigate = useCallback((delta) => {
    setDirection(delta)
    setCurrent((prev) => (prev + delta + total) % total)
  }, [total])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') navigate(-1)
      if (e.key === 'ArrowRight') navigate(1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, navigate, onClose])

  // Body scroll lock — iOS-safe (position:fixed method preserves scroll position)
  useEffect(() => {
    if (!isOpen) return
    const scrollY = window.scrollY
    const body = document.body
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.width = '100%'
    body.style.overflowY = 'scroll'
    return () => {
      body.style.position = ''
      body.style.top = ''
      body.style.width = ''
      body.style.overflowY = ''
      window.scrollTo(0, scrollY)
    }
  }, [isOpen])

  // Auto-scroll thumbnail strip — scrollIntoView is reliable across all browsers/iOS
  useEffect(() => {
    if (!stripRef.current || !isOpen) return
    const target = stripRef.current.children[current]
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
  }, [current, isOpen])

  // Reset to initial image when opened
  useEffect(() => {
    if (isOpen) setCurrent(initialIndex)
  }, [isOpen, initialIndex])

  // Touch / pointer swipe — lower threshold for mobile feel
  const pointerStart = useRef(null)
  const onPointerDown = (e) => { pointerStart.current = e.clientX }
  const onPointerUp = (e) => {
    if (pointerStart.current === null) return
    const delta = pointerStart.current - e.clientX
    if (Math.abs(delta) > 40) navigate(delta > 0 ? 1 : -1)
    pointerStart.current = null
  }

  const variants = {
    enter: (d) => ({ x: d > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? -60 : 60, opacity: 0 }),
  }

  // NOTE: no early return before AnimatePresence — it would kill the exit animation

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="gallery-backdrop"
          className="fixed inset-0 z-[9000] flex flex-col"
          style={{ overscrollBehavior: 'contain' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 backdrop-blur-sm bg-black/95"
            onClick={onClose}
          />

          {/* h-full fills fixed inset-0 parent — covers full viewport on all devices including iOS Safari */}
          <div className="flex relative z-10 flex-col h-full select-none">
            {/* Top bar — padded for notch / Dynamic Island */}
            <div
              className="flex justify-between items-center px-4 pb-2 shrink-0 sm:px-6"
              style={{ paddingTop: 'max(14px, env(safe-area-inset-top))' }}
            >
              <span className="font-mono text-sm tracking-widest text-white/40">
                {loading && total === 0 ? '-- / --' : `${String(current + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`}
              </span>
              <button
                onClick={onClose}
                className="flex justify-center items-center w-11 h-11 rounded-full transition-all text-white/60 hover:text-white active:text-white hover:bg-white/10 active:bg-white/20"
                aria-label="Close gallery"
              >
                <FaTimes size={18} />
              </button>
            </div>

            {/* Main image area
                - touch-action:none prevents iOS from scrolling the page during swipe
                - overflow:hidden clips the slide animation at edges
            */}
            <div
              className="flex overflow-hidden relative flex-1 justify-center items-center px-12 sm:px-14 md:px-20"
              onPointerDown={onPointerDown}
              onPointerUp={onPointerUp}
              style={{ touchAction: 'none' }}
            >
              {loading && total === 0 && (
                <div className="flex flex-col gap-3 items-center text-white/50">
                  <svg className="w-10 h-10 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                  </svg>
                </div>
              )}
              {/* Prev arrow */}
              {total > 1 && (
                <button
                  onClick={() => navigate(-1)}
                  className="flex absolute left-1 z-20 justify-center items-center w-10 h-10 text-white rounded-full transition-all sm:left-2 md:left-3 sm:w-11 sm:h-11 bg-white/10 hover:bg-white/25 active:bg-white/30"
                  aria-label="Previous image"
                >
                  <FaChevronLeft size={15} />
                </button>
              )}

              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={current}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3, ease: [0.32, 0, 0.18, 1] }}
                  className="flex justify-center items-center w-full h-full"
                >
                  <img
                    src={images[current]}
                    alt={`Gallery image ${current + 1}`}
                    className="object-contain max-w-full max-h-full rounded-lg shadow-2xl"
                    style={{
                      maxHeight: 'min(calc(100dvh - 190px), calc(100vh - 190px))',
                    }}
                    draggable={false}
                  />
                </motion.div>
              </AnimatePresence>

              {/* Next arrow */}
              {total > 1 && (
                <button
                  onClick={() => navigate(1)}
                  className="flex absolute right-1 z-20 justify-center items-center w-10 h-10 text-white rounded-full transition-all sm:right-2 md:right-3 sm:w-11 sm:h-11 bg-white/10 hover:bg-white/25 active:bg-white/30"
                  aria-label="Next image"
                >
                  <FaChevronRight size={15} />
                </button>
              )}
            </div>

            {/* Thumbnail strip + progress
                - padded for home indicator (iPhone bottom bar)
                - WebkitOverflowScrolling: smooth momentum scroll on iOS
                - scrollbar hidden via inline style (Firefox/Edge) + ::-webkit-scrollbar (injected below)
            */}
            {total > 1 && (
              <div
                className="px-3 pt-3 shrink-0"
                style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
              >
                <div className="mx-auto mb-3 h-[2px] bg-white/10 rounded-full overflow-hidden" style={{ maxWidth: 280 }}>
                  <motion.div
                    className="h-full rounded-full bg-primary"
                    animate={{ width: `${((current + 1) / total) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>

                <div
                  ref={stripRef}
                  className="flex overflow-x-auto gap-2 justify-start gallery-strip sm:gap-3 sm:justify-center"
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {/* Hides webkit scrollbar — injected as scoped style; class gallery-strip targets this element only */}
                  <style>{`.gallery-strip::-webkit-scrollbar { display: none; }`}</style>
                  {images.map((src, i) => (
                    <button
                      key={i}
                      onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i) }}
                      className={`flex-shrink-0 rounded overflow-hidden transition-all duration-200 ${
                        i === current
                          ? 'ring-2 ring-primary scale-110 opacity-100'
                          : 'opacity-40 hover:opacity-70 active:opacity-80 hover:scale-105'
                      }`}
                      style={{ width: THUMB_W, height: THUMB_H }}
                      aria-label={`Go to image ${i + 1}`}
                    >
                      <img
                        src={src}
                        alt=""
                        className="object-cover w-full h-full"
                        draggable={false}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
